import { useSyncExternalStore } from 'react';
import { toast } from 'sonner';
import { logger } from '@/lib/logger';

export interface ReferenceLoadIssueSnapshot {
  totalMissingIdCount: number;
  totalDuplicateCount: number;
  affectedEndpoints: string[];
}

const emptySnapshot: ReferenceLoadIssueSnapshot = {
  totalMissingIdCount: 0,
  totalDuplicateCount: 0,
  affectedEndpoints: [],
};

let snapshot: ReferenceLoadIssueSnapshot = emptySnapshot;
const listeners = new Set<() => void>();

const notifyListeners = () => {
  listeners.forEach((listener) => listener());
};

export const recordReferenceLoadIssues = ({
  endpoint,
  skippedMissingIdCount,
  skippedDuplicateCount,
}: {
  endpoint: string;
  skippedMissingIdCount: number;
  skippedDuplicateCount: number;
}) => {
  if (skippedMissingIdCount === 0 && skippedDuplicateCount === 0) {
    return;
  }

  logger.warn('Reference data load issues', {
    endpoint,
    skippedMissingIdCount,
    skippedDuplicateCount,
  });

  snapshot = {
    totalMissingIdCount: snapshot.totalMissingIdCount + skippedMissingIdCount,
    totalDuplicateCount: snapshot.totalDuplicateCount + skippedDuplicateCount,
    affectedEndpoints: snapshot.affectedEndpoints.includes(endpoint)
      ? snapshot.affectedEndpoints
      : [...snapshot.affectedEndpoints, endpoint],
  };
  notifyListeners();

  const issueParts: string[] = [];
  if (skippedMissingIdCount > 0) {
    issueParts.push(`${skippedMissingIdCount} missing ID${skippedMissingIdCount === 1 ? '' : 's'}`);
  }
  if (skippedDuplicateCount > 0) {
    issueParts.push(`${skippedDuplicateCount} duplicate${skippedDuplicateCount === 1 ? '' : 's'}`);
  }

  toast.error(
    `Some ${endpoint} items could not be loaded (${issueParts.join(', ')}).`,
    { id: `builder-reference-load-${endpoint}` }
  );
};

export const subscribeReferenceLoadIssues = (listener: () => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

export const getReferenceLoadIssueSnapshot = () => snapshot;

export const useReferenceLoadIssues = () =>
  useSyncExternalStore(
    subscribeReferenceLoadIssues,
    getReferenceLoadIssueSnapshot,
    getReferenceLoadIssueSnapshot
  );

export const getReferenceLoadIssueCount = (issues: ReferenceLoadIssueSnapshot) =>
  issues.totalMissingIdCount + issues.totalDuplicateCount;
